"use strict";
// Class name helpers shared by the backend (charts, class history) and the
// frontend. See documentation/participant-class-history.md ("Schulstufe").
//
// Austrian Schulstufen 1-13 (Gerald, 10/2026):
//   Volksschule  Stufe 1-4  = 1.-4. Klasse
//   Unterstufe   Stufe 5-8  = 1.-4. Klasse
//   Oberstufe    Stufe 9-13 = 5.-9. Klasse
// A class name holds the Klasse (leading number), not the Schulstufe. Klasse
// 1-4 exists in the Volksschule and in the Unterstufe, so the age at the test
// decides between them (no school type is stored).
//
// The backfill of migration 20261013100000_add_participant_class_history
// stored the old rule (first 1-2 digits, no range check) in
// participant_class_history."schoolLevel". The proposed SQL repair is in
// documentation/participant-class-history.md; it must go into a NEW migration.
Object.defineProperty(exports, "__esModule", { value: true });
exports.PRIMARY_AGE_OFFSET = exports.CLASS_NUMBER_MAX = exports.SCHOOL_LEVEL_MAX = exports.SCHOOL_LEVEL_MIN = void 0;
exports.classNumberFromClassName = classNumberFromClassName;
exports.schoolLevelFromClassNumber = schoolLevelFromClassNumber;
exports.schoolLevelFromClassName = schoolLevelFromClassName;
exports.schoolStageOf = schoolStageOf;
exports.normalizeClassName = normalizeClassName;
exports.SCHOOL_LEVEL_MIN = 1;
exports.SCHOOL_LEVEL_MAX = 13;
/** Highest Klasse number in a class name (Oberstufe 9. Klasse = Stufe 13). */
exports.CLASS_NUMBER_MAX = 9;
/**
 * Klasse 1-4: a child younger than `classNumber + 8` years at the test is in
 * the Volksschule, otherwise in the Unterstufe. Typical ages are about
 * Klasse + 6 (Volksschule) and Klasse + 10 (Unterstufe), so the threshold
 * leaves about 2 years for early school entry or repeated years.
 */
exports.PRIMARY_AGE_OFFSET = 8;
/**
 * Klasse from the class name: the leading number 1-9 ("3B" -> 3, "04a" -> 4).
 * Anything else has no Klasse: "25a", "2526b", "10a", "123abc", "B3", "".
 */
function classNumberFromClassName(className) {
    const match = className?.trim().match(/^0*([1-9])(?!\d)/);
    return match ? Number(match[1]) : null;
}
/**
 * Schulstufe (1-13) from a Klasse and the age at the test (full or exact
 * years). Klasse 5-9 is always the Oberstufe (Stufe 9-13). Klasse 1-4 needs
 * the age; without it the Stufe is unknown (null).
 */
function schoolLevelFromClassNumber(classNumber, ageYears) {
    if (classNumber === null ||
        !Number.isInteger(classNumber) ||
        classNumber < 1 ||
        classNumber > exports.CLASS_NUMBER_MAX) {
        return null;
    }
    if (classNumber >= 5)
        return classNumber + 4;
    if (ageYears === null || ageYears === undefined || !Number.isFinite(ageYears))
        return null;
    return ageYears < classNumber + exports.PRIMARY_AGE_OFFSET
        ? classNumber
        : classNumber + 4;
}
/** Schulstufe (1-13) from the class name and the age at the test. */
function schoolLevelFromClassName(className, ageYears) {
    return schoolLevelFromClassNumber(classNumberFromClassName(className), ageYears);
}
/** Stage and Klasse of a Schulstufe (for labels), null outside 1-13. */
function schoolStageOf(level) {
    if (!Number.isInteger(level) ||
        level < exports.SCHOOL_LEVEL_MIN ||
        level > exports.SCHOOL_LEVEL_MAX) {
        return null;
    }
    if (level <= 4)
        return { stage: "primary", classNumber: level };
    return { stage: level <= 8 ? "lower" : "upper", classNumber: level - 4 };
}
/** Stored form of a class name: trimmed, empty -> null. */
function normalizeClassName(className) {
    const trimmed = className?.trim();
    return trimmed ? trimmed : null;
}
